import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from './icons';

/**
 * Status course with numbered dots and connecting lines (design/Abrechnungen "Status-Verlauf").
 * Done steps show a check, the current one a ring (amber when `tone="warn"`), later ones a grey number.
 */
export function ProcessSteps({ steps, current, tone, label }: { steps: string[]; current: number; tone?: 'warn'; label: string }) {
  return (
    <ol className={`process-steps ${tone ?? ''}`} aria-label={label} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        return (
          <li key={s} className={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="ps-dot" aria-hidden>{state === 'done' ? <Icon name="check" size={13} strokeWidth={2.6} /> : i + 1}</span>
            <span className="ps-label">{s}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Phase bar of a running job (design/BelegUpload "Ablauf"): one segment per phase with its label below.
 * Finished phases are green, the running one petrol, waiting ones grey.
 */
export function PhaseSteps({ steps, current, label }: { steps: string[]; current: number; label: string }) {
  return (
    <ol className="phase-steps" aria-label={label} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        return (
          <li key={s} className={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="ph-bar" aria-hidden />
            <span className="ph-label">{s}</span>
          </li>
        );
      })}
    </ol>
  );
}

export type MarkTone = 'ok' | 'warn' | 'err' | 'neutral';
/** Round status mark (check, exclamation, cross) next to a check item; colour never alone, the row text says it. */
export function StatusMark({ tone, size = 24, pop }: { tone: MarkTone; size?: number; pop?: boolean }) {
  return (
    <span className={`status-mark ${tone} ${pop ? 'success-pop' : ''}`} style={{ width: size, height: size }} aria-hidden>
      {tone === 'ok' ? <Icon name="check" size={Math.round(size * 0.58)} strokeWidth={2.6} /> : tone === 'err' ? <Icon name="close" size={Math.round(size * 0.5)} strokeWidth={2.6} /> : tone === 'warn' ? '!' : '·'}
    </span>
  );
}

/** "← Back" link above a page title (design: "← Belege 2025", "← Abrechnung 2025 · Lindenstraße 12"). */
export const BackLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="back-link">
    <Icon name="arrowRight" size={15} strokeWidth={1.8} className="flip" />
    <span>{children}</span>
  </Link>
);

/**
 * Inline chip filter with a leading label (design/Abrechnungen "Objekt · Status", design/BelegeArchiv status chips).
 * Single choice as radiogroup; `count` is shown after the label, `tone="warn"` tints the chip (e.g. "Zu prüfen 3").
 */
export function ChipFilter<V extends string>({ label, labelHidden, options, value, onChange }: { label: string; labelHidden?: boolean; options: { value: V; label: string; count?: number; tone?: 'warn' }[]; value: V; onChange: (v: V) => void }) {
  return (
    <div className="chip-filter" role="radiogroup" aria-label={label}>
      {!labelHidden && <span className="cf-label" aria-hidden>{label}</span>}
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className={`chip ${o.tone ?? ''}`} onClick={() => onChange(o.value)}>
          {o.label}
          {o.count != null && <span className="cf-count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}
